import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  FileImage,
  Paperclip,
  Save,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import {
  CreateKnowledgeArticlePayload,
  knowledgeApi,
  UploadedKnowledgeMedia,
} from '@/api/endpoints';
import { useAuthStore } from '@/stores/authStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import PageHero from '@/components/ui/PageHero';
import { sanitizeArticleHtml } from './sanitizeArticleHtml';
import { canManageKnowledgeArticles } from './permissions';

const MAX_SCREENSHOT_SIZE = 25 * 1024 * 1024;
const IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const transliteration: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const slugify = (value: string) => {
  const transliterated = value
    .trim()
    .toLowerCase()
    .split('')
    .map((char) => transliteration[char] ?? char)
    .join('');

  return (
    transliterated
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-')
      .slice(0, 90) || 'article'
  );
};

const parseTags = (value: string) =>
  Array.from(
    new Set(
      value
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
        .slice(0, 50),
    ),
  );

const imageMarkdown = (media: UploadedKnowledgeMedia) =>
  `![${media.fileName || 'screenshot'}](${media.url})`;

const appendImageToText = (current: string, media: UploadedKnowledgeMedia) => {
  const block = imageMarkdown(media);
  return current.trim() ? `${current.trimEnd()}\n\n${block}\n` : `${block}\n`;
};

const renderInlineText = (value: string) =>
  escapeHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');

const renderFigure = (alt: string, url: string) => {
  const safeAlt = escapeHtml(alt || 'Скриншот');
  const safeUrl = escapeHtml(url);
  return `<figure><img src="${safeUrl}" alt="${safeAlt}" title="${safeAlt}" /><figcaption>${safeAlt}</figcaption></figure>`;
};

const renderBlock = (block: string) => {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return '';

  if (lines.length === 1) {
    const heading = lines[0].match(/^#{2,3}\s+(.+)$/);
    if (heading) {
      return `<h2>${renderInlineText(heading[1])}</h2>`;
    }
  }

  const imageBlocks = lines
    .map((line) => line.match(/^!\[(.*?)]\((.*?)\)$/))
    .filter((match): match is RegExpMatchArray => Boolean(match));
  if (imageBlocks.length === lines.length) {
    return imageBlocks.map((match) => renderFigure(match[1], match[2])).join('');
  }

  if (lines.every((line) => /^[-*]\s+/.test(line))) {
    return `<ul>${lines
      .map((line) => `<li>${renderInlineText(line.replace(/^[-*]\s+/, ''))}</li>`)
      .join('')}</ul>`;
  }

  if (lines.every((line) => /^\d+[.)]\s+/.test(line))) {
    return `<ol>${lines
      .map((line) => `<li>${renderInlineText(line.replace(/^\d+[.)]\s+/, ''))}</li>`)
      .join('')}</ol>`;
  }

  return `<p>${lines.map(renderInlineText).join('<br />')}</p>`;
};

const buildArticleHtml = (body: string, screenshots: UploadedKnowledgeMedia[]) => {
  const blocks = body
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  const html = blocks.map(renderBlock).join('');
  const referencedUrls = new Set(
    Array.from(body.matchAll(/!\[.*?]\((.*?)\)/g)).map((match) => match[1]),
  );
  const unattached = screenshots.filter((media) => !referencedUrls.has(media.url));

  if (!unattached.length) return html;

  const gallery = [
    html,
    '<h2>Скриншоты</h2>',
    ...unattached.map((media) => renderFigure(media.fileName, media.url)),
  ];

  return gallery.filter(Boolean).join('');
};

const KnowledgeArticleEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user, isLoading: authLoading } = useAuthStore();
  const canManageKnowledge = canManageKnowledgeArticles(user);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [body, setBody] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [screenshots, setScreenshots] = useState<UploadedKnowledgeMedia[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['knowledge-categories'],
    queryFn: knowledgeApi.getCategories,
    enabled: canManageKnowledge,
  });

  useEffect(() => {
    if (!categoryId && categories.length > 0) {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);

  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugify(title));
    }
  }, [slugTouched, title]);

  const articleHtml = useMemo(() => buildArticleHtml(body, screenshots), [body, screenshots]);
  const previewHtml = useMemo(() => sanitizeArticleHtml(articleHtml), [articleHtml]);
  const selectedCategory = categories.find((category) => category.id === categoryId);
  const tags = useMemo(() => parseTags(tagsInput), [tagsInput]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateKnowledgeArticlePayload) => knowledgeApi.createArticle(payload),
    onSuccess: (article) => {
      toast.success(isPublished ? 'Статья опубликована' : 'Черновик сохранён');
      queryClient.invalidateQueries({ queryKey: ['knowledge-all-articles'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-categories'] });
      navigate(`/knowledge/${article.slug}`, { replace: true, state: { from: '/knowledge' } });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Не удалось сохранить статью');
    },
  });

  const validate = () => {
    if (title.trim().length < 3) {
      toast.error('Заголовок должен быть не короче 3 символов');
      return false;
    }

    if (!SLUG_PATTERN.test(slug.trim())) {
      toast.error('Slug должен быть на латинице: буквы, цифры и дефисы');
      return false;
    }

    if (!categoryId) {
      toast.error('Выбери категорию статьи');
      return false;
    }

    if (excerpt.length > 500) {
      toast.error('Краткое описание не должно быть длиннее 500 символов');
      return false;
    }

    if (body.replace(/!\[.*?]\(.*?\)/g, '').trim().length < 10) {
      toast.error('Добавь основной текст статьи');
      return false;
    }

    return true;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    createMutation.mutate({
      title: title.trim(),
      slug: slug.trim(),
      categoryId,
      excerpt: excerpt.trim() || undefined,
      content: articleHtml,
      type: 'public',
      isPublished,
      sortOrder: 0,
      tags,
      demoSteps: null,
      clips: null,
    });
  };

  const handleScreenshotUpload = async (files: FileList | null) => {
    if (!files?.length) return;

    const selectedFiles = Array.from(files);
    setIsUploading(true);

    try {
      for (const file of selectedFiles) {
        if (!IMAGE_MIME_TYPES.has(file.type)) {
          toast.error(`Неподдерживаемый формат: ${file.name}`);
          continue;
        }

        if (file.size > MAX_SCREENSHOT_SIZE) {
          toast.error(`Файл больше 25 МБ: ${file.name}`);
          continue;
        }

        const media = await knowledgeApi.uploadDemoMedia(file);
        setScreenshots((current) => [...current, media]);
        setBody((current) => appendImageToText(current, media));
      }

      toast.success('Скриншоты прикреплены');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Не удалось загрузить скриншот');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeScreenshot = (media: UploadedKnowledgeMedia) => {
    setScreenshots((current) => current.filter((item) => item.url !== media.url));
    setBody((current) =>
      current
        .replace(new RegExp(`\\n?${imageMarkdown(media).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\n?`, 'g'), '\n')
        .replace(/\n{3,}/g, '\n\n'),
    );
  };

  if (authLoading && !user) {
    return <LoadingSpinner text="Проверка доступа..." />;
  }

  if (!canManageKnowledge) {
    return (
      <div className="animate-fade-in space-y-6">
        <Link to="/knowledge" className="inline-flex items-center gap-2 text-sm text-[#a5b4ff] hover:underline">
          <ArrowLeft className="h-4 w-4" />
          Назад к базе знаний
        </Link>
        <Card padding="lg" className="cc-glass">
          <div className="flex items-start gap-4">
            <AlertCircle className="mt-1 h-5 w-5 text-[#a5b4ff]" />
            <div>
              <h1 className="cc-display text-xl font-bold text-[#e7eaf3]">Недостаточно прав</h1>
              <p className="mt-2 text-sm text-[#9aa3bd]">
                Создавать статьи могут только сотрудники с правом редактирования базы знаний.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="animate-fade-in space-y-6 md:space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/knowledge" className="inline-flex items-center gap-2 text-sm text-[#a5b4ff] hover:underline">
          <ArrowLeft className="h-4 w-4" />
          Назад к базе знаний
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            icon={<Eye className="h-4 w-4" />}
            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          >
            Предпросмотр
          </Button>
          <Button
            type="submit"
            icon={<Save className="h-4 w-4" />}
            loading={createMutation.isPending}
            disabled={isUploading || createMutation.isPending}
          >
            Сохранить
          </Button>
        </div>
      </div>

      <PageHero
        eyebrow="CLUB:CORE · БАЗА ЗНАНИЙ"
        title="Создать статью"
        subtitle="Материал для клиентской базы знаний с черновиком, публикацией и скриншотами."
        ghost="DOCS"
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <Card padding="lg" className="cc-glass kb-editor-panel">
          <div className="grid gap-5">
            <label className="kb-editor-field">
              <span>Заголовок</span>
              <input
                className="kb-editor-control"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Например: Как проверить диск D на рабочей станции"
                maxLength={255}
              />
            </label>

            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(220px,320px)]">
              <label className="kb-editor-field">
                <span>Slug</span>
                <input
                  className="kb-editor-control font-mono text-sm"
                  value={slug}
                  onChange={(event) => {
                    setSlugTouched(true);
                    setSlug(slugify(event.target.value));
                  }}
                  placeholder="kak-proverit-disk-d"
                  maxLength={255}
                />
              </label>

              <label className="kb-editor-field">
                <span>Категория</span>
                <select
                  className="kb-editor-control"
                  value={categoryId}
                  onChange={(event) => setCategoryId(event.target.value)}
                  disabled={categoriesLoading}
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="kb-editor-field">
              <span>Краткое описание</span>
              <textarea
                className="kb-editor-control kb-editor-textarea min-h-[96px]"
                value={excerpt}
                onChange={(event) => setExcerpt(event.target.value)}
                placeholder="Один-два предложения, которые помогут понять, о чём статья."
                maxLength={500}
              />
            </label>

            <label className="kb-editor-field">
              <span>Текст статьи</span>
              <textarea
                className="kb-editor-control kb-editor-textarea min-h-[360px] font-mono text-sm leading-7"
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder={'## Что проверить\n\nОпиши шаги. Для списков используй строки с "-". Скриншоты вставятся сюда автоматически после загрузки.'}
              />
            </label>

            <label className="kb-editor-field">
              <span>Теги</span>
              <input
                className="kb-editor-control"
                value={tagsInput}
                onChange={(event) => setTagsInput(event.target.value)}
                placeholder="disk d, uwf, vpn"
              />
            </label>
          </div>
        </Card>

        <div className="space-y-5 xl:sticky xl:top-24 self-start">
          <Card padding="lg" className="cc-glass kb-editor-side">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="kb-editor-side-title">Публикация</div>
                <p className="mt-1 text-xs text-[#9aa3bd]">
                  {isPublished ? 'Будет видна клиентам после сохранения.' : 'Сохранится как черновик.'}
                </p>
              </div>
              <button
                type="button"
                className={`kb-editor-switch${isPublished ? ' is-on' : ''}`}
                onClick={() => setIsPublished((value) => !value)}
                aria-pressed={isPublished}
              >
                <span />
              </button>
            </div>

            <div className="mt-5 grid gap-3 text-sm">
              <div className="kb-editor-meta-row">
                <span>Категория</span>
                <strong>{selectedCategory?.name || 'Не выбрана'}</strong>
              </div>
              <div className="kb-editor-meta-row">
                <span>Теги</span>
                <strong>{tags.length}</strong>
              </div>
              <div className="kb-editor-meta-row">
                <span>Скриншоты</span>
                <strong>{screenshots.length}</strong>
              </div>
            </div>
          </Card>

          <Card padding="lg" className="cc-glass kb-editor-side">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="kb-editor-side-title">Скриншоты</div>
                <p className="mt-1 text-xs text-[#9aa3bd]">PNG, JPG, WebP или GIF до 25 МБ.</p>
              </div>
              <FileImage className="h-5 w-5 text-[#a5b4ff]" />
            </div>

            <label className="kb-editor-dropzone mt-4">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(event) => handleScreenshotUpload(event.target.files)}
              />
              <UploadCloud className="h-5 w-5" />
              <span>{isUploading ? 'Загрузка...' : 'Прикрепить скриншоты'}</span>
            </label>

            {screenshots.length > 0 && (
              <div className="mt-4 space-y-2">
                {screenshots.map((media) => (
                  <div key={media.url} className="kb-editor-attachment">
                    <div className="min-w-0 flex items-center gap-2">
                      <Paperclip className="h-4 w-4 flex-shrink-0 text-[#a5b4ff]" />
                      <span className="truncate">{media.fileName}</span>
                    </div>
                    <button type="button" onClick={() => removeScreenshot(media)} aria-label="Удалить скриншот">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <Card padding="lg" className="cc-glass kb-editor-preview">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="kb-editor-side-title">Предпросмотр</div>
            <p className="mt-1 text-sm text-[#9aa3bd]">{excerpt || 'Краткое описание появится здесь.'}</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-fill-2 px-3 py-1 text-xs text-[#9aa3bd]">
            <CheckCircle2 className="h-3.5 w-3.5 text-[#a5b4ff]" />
            {isPublished ? 'Публикация' : 'Черновик'}
          </span>
        </div>

        {previewHtml ? (
          <div className="kb-article-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        ) : (
          <div className="rounded-xl border border-dashed border-line bg-fill-2 p-5 text-sm text-[#9aa3bd]">
            Текст статьи появится в предпросмотре после ввода.
          </div>
        )}
      </Card>
    </form>
  );
};

export default KnowledgeArticleEditorPage;
